import { useEffect, useState } from 'react';

/**
 * Reports `active` only once it has held for `delayMs`, so a state that usually
 * resolves quickly does not flash its indicator. The delay is measured from the
 * last change of `resetKey` rather than from `active`, so a brief dip in
 * `active` while one thing hands off to the next neither restarts the wait nor
 * hides an indicator already shown.
 */
export function useDelayedFlag(
  active: boolean,
  delayMs: number,
  resetKey: string | null
): boolean {
  const [elapsed, setElapsed] = useState(false);
  const [lastResetKey, setLastResetKey] = useState(resetKey);

  // Clear the elapsed wait during the render the key changes on, not after it in
  // an effect, so the previous key's finished wait cannot paint for a frame
  // before the new one starts.
  if (resetKey !== lastResetKey) {
    setLastResetKey(resetKey);
    setElapsed(false);
  }

  useEffect(() => {
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [resetKey, delayMs]);

  return active && elapsed;
}
